import { Component, EventEmitter, inject, input, Input, OnChanges, OnInit, output, Output, signal, SimpleChanges, TemplateRef, ViewChild } from "@angular/core";
import { AbstractControl, ControlValueAccessor, FormBuilder, FormGroup, ValidationErrors, Validator, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { AuthService } from "@app/common/auth.service";
import { BaseComponent } from "@app/components/base/base.component";
import { ModalService } from "@app/components/modal/modal.service";
import { FinancialCalculatorService } from "@app/pages/quote/open-quote/services/financial-calculator.service";
import { ClientsService } from "@app/pages/clients/services/clients.service";
import { MenusServices } from "@app/services/menus.service";
import { NotificationService } from "@app/services/notifications.service";
import { ClientApi, PersonType } from "@app/pages/clients/interfaces/client";
import { DOCUMENT } from "@angular/common";
import { FormStep } from "@app/components/client-form-modal-wrapper/client-form-modal-wrapper.component";
import { interval } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { environment } from "@env/environment.local";
import { naturalPersonNameValidator } from "@app/core/util/name-validator";
import { coerceOpenQuoteBoolean } from "@app/pages/quote/open-quote/model/open-quote.model";

// Cambio para merge
@Component({
  template: '',
})
export class ClientOpenQuoteWrapper
  extends BaseComponent
  implements OnInit, OnChanges, ControlValueAccessor, Validator
{
  // SERVICES
  private readonly fb = inject(FormBuilder);
  public readonly authService = inject(AuthService);
  public readonly financialCalculator = inject(FinancialCalculatorService);
  public readonly clientsService = inject(ClientsService);
  private readonly notificationService = inject(NotificationService);
  formCustomer: FormGroup = this.fb.group({});

  @Output() editingStarted = new EventEmitter<string>();
  selectedCustomerId = output<number>();
  @Input() customer: any;
  /** Misma referencia que las propuestas en edición: para etiqueta Loyalty y limpiar flags al marcar Feria. */
  @Input() deals: any[] | null = null;
  @Input() userId: any;
  @Input() isModeEdition: boolean;
  /** Cotización terminal: forzar UI editable de cliente aunque isModeEdition sea true. */
  @Input() customerOnlyUnlock = false;
  /** Estado final o en revisión: bloquear edición de cliente. */
  @Input() quoteContentReadOnly = false;

  customerIdSaved = input<number | null>(null);
  @ViewChild('modalNewClient') modalNewClient!: TemplateRef<any>;
  @ViewChild('modalSelectClient') modalSelectClient!: TemplateRef<any>;

  readonly modalService = inject(ModalService);
  private readonly document = inject(DOCUMENT);

  // SIGNALS
  readonly selectedClient = signal<any>(null);
  readonly tempSelectedClient = signal<any>(null);
  readonly isClientWithRFC = signal<boolean>(false);

  customerCopy: any = null;
  isClientWithRFCCopy: boolean | null = null;
  customerIdCopy: number | null = null;
  public readonly clientCatalogActive = environment.active_client_catalog;
  readonly featureLoyaltyFeriaQuotation = environment.featureLoyaltyFeriaQuotation;


  constructor(
    readonly router: ActivatedRoute,
    readonly menu: MenusServices,
    notification: NotificationService,
  ) {
    super(router, menu, notification);
    this.createForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customer'] && this.customer) {
      this.validatorChange();
      if (environment.featureLoyaltyFeriaQuotation) {
        this.formCustomer.patchValue(
          {
            feriaCustomer: coerceOpenQuoteBoolean(this.customer?.feriaCustomer),
          },
          { emitEvent: false },
        );
      }
      if(this.customerIdSaved() && this.clientCatalogActive){
        const normalized = this.normalizeCustomerPatch(this.customer);
        this.selectedClient.set({ ...normalized, id: this.customerIdSaved(), customerName: normalized?.customerName || normalized?.name });
        this.isClientWithRFC.set(true);
        this.formCustomer.controls['isFromProLeaseNet'].setValue(this.customer?.isFromProLeaseNet, { emitEvent: false });
        this.formCustomer.controls['lastName'].setValidators(null);
        this.formCustomer.controls['lastName'].setErrors(null);
        this.formCustomer.get('lastName')?.updateValueAndValidity({ emitEvent: false });
      }
    }
    if (changes['userId']) {
      if (this.userId == undefined) return;
      if (this.userId != this.authService.currentUserIds) {
        this.formCustomer.controls['customerType'].disable();
        this.formCustomer.controls['name'].disable();
        this.formCustomer.controls['dfsRating'].disable();
        this.formCustomer.controls['personType'].disable();
        this.formCustomer.controls['secondName'].disable();
        this.formCustomer.controls['lastName'].disable();
        this.formCustomer.controls['mothersLastName'].disable();
        this.formCustomer.controls['feriaCustomer']?.disable();
      }
    }
  }

  ngOnInit() {
    // interval(8000).subscribe(() => {
    //   console.log({ raw: this.formCustomer?.getRawValue?.()});
    //   for (const key in this.formCustomer.controls) {
    //     const control = this.formCustomer.controls[key];
    //     if (control.invalid) {
    //       console.log(`El campo ${key} tiene el siguiente error:`, control.errors);
    //     }
    //   }
    // });
  }

  createForm() {
    this.formCustomer = this.fb.group({
      id: [null, []],
      dfsRating: [, [Validators.required, Validators.pattern('^(10|[1-9])$')]],
      creatioDate: [null, []],
      customerType: [, [Validators.required]],
      personType: [, [Validators.required]],
      rfc: [, []],
      name: [, [Validators.required]],
      secondName: [, []],
      lastName: [, []],
      mothersLastName: [, []],
      isFromProLeaseNet: [false, []],
      feriaCustomer: [false, []],
    });

    this.formCustomer.valueChanges.subscribe((value) => {
      this.onChange(value);
      this.onTouched();
    });

    if (environment.featureLoyaltyFeriaQuotation) {
      this.formCustomer
        .get('feriaCustomer')
        ?.valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe((feria) => {
          if (coerceOpenQuoteBoolean(feria) && Array.isArray(this.deals)) {
            for (const d of this.deals) {
              if (d && typeof d === 'object') {
                d.dealIsLoyalty = false;
              }
            }
          }
        });
    }
  }

  updateClientForm(client: ClientApi | null, validateRequiredFields = false) {
    if (this.document) {
      this.document.body.style.overflow = 'auto';
    }
    if(client !== null){
      const applyWithData = (data: ClientApi) => {
        const normalized = this.normalizeCustomerPatch(data) as any;
        if (validateRequiredFields) {
          const missing = this.getMissingRequiredFields(normalized, this.isClientWithRFC());
          if (missing.length > 0) {
            this.notificationService.showMessage(
              [`Este cliente no cumple los requisitos. Falta: ${missing.join(', ')}`],
              'error',
            );
          }
        }

        const dto = {
          personType: normalized.personType,
          name: normalized.name,
          lastName: normalized.lastName,
          mothersLastName: normalized.mothersLastName,
          secondName: normalized.secondName || null,
          rfc: normalized.rfc,
          isFromProLeaseNet: Boolean(normalized.isFromProLeaseNet),
          feriaCustomer: environment.featureLoyaltyFeriaQuotation
            ? coerceOpenQuoteBoolean((normalized as any)?.feriaCustomer)
            : false,
        }
        this.customerCopy = null;
        this.isClientWithRFCCopy = null;
        this.formCustomer.patchValue(dto);
        this.selectedClient.set(normalized);
        if(normalized.id !== null) {
          this.selectedCustomerId.emit(Number(normalized.id));
        }
        this.validatorChange();
        this.formCustomer.controls['lastName'].setValidators(null);
        this.formCustomer.controls['lastName'].setErrors(null);
        this.formCustomer.get('lastName')?.updateValueAndValidity();
      };

      // Re-verificar por RFC: /customers/all suele devolver customerName concatenado.
      if (
        client.rfc &&
        client.personType === PersonType.NATURAL_PERSON
      ) {
        this.clientsService.searchClientByRFC(client.rfc)
          .pipe(takeUntilDestroyed(this._destroyRef))
          .subscribe({
            next: (response: any) => {
              const verified = response?.data as Partial<ClientApi> | null;
              applyWithData(
                verified?.rfc ? ({ ...client, ...verified } as ClientApi) : client,
              );
            },
            error: () => applyWithData(client),
          });
        return;
      }

      applyWithData(client);
    }
  }

  /**
   * Devuelve los campos obligatorios que faltan en el cliente.
   */
  private getMissingRequiredFields(client: any, hasRfc: boolean): string[] {
    const missing: string[] = [];

    if (hasRfc && !(client.rfc || '').toString().trim()) {
      missing.push('RFC');
    }

    const isNaturalPerson = client.personType === PersonType.NATURAL_PERSON;

    if (isNaturalPerson) {
      const firstName = (client.firstName || client.name || client.customerName || '').toString().trim();
      if (!firstName) {
        missing.push('Nombre');
      }
      if (!(client.lastName || '').toString().trim()) {
        missing.push('Apellido paterno');
      }
    } else {
      const customerName = (client.customerName || client.firstName || client.name || '').toString().trim();
      if (!customerName) {
        missing.push('Razón social');
      }
    }

    return missing;
  }

  saveAndUpdateClientForm(event: { client: ClientApi | null; isNew: boolean; isUpdated: boolean }) {
    this.updateClientForm(event.client, event.isNew || event.isUpdated);
    this.tempSelectedClient.set(null);
    if(event.isNew){
      this.clientsService.showSaveClientSuccess();
    }
  }

  selectExistingClient(client: ClientApi): void {
    this.saveAndUpdateClientForm({ client, isNew: false, isUpdated: false });
  }

  cancelModal(event: any) {
    if(event === FormStep.STEP_RFC_SEARCH || event === FormStep.STEP_NEW_CLIENT){
      this.selectedClient.set(this.tempSelectedClient());
      this.tempSelectedClient.set(null);
    }
  }

  validatorChange() {
    const personType = this.formCustomer.controls['personType'].value;
    const isNaturalPerson = personType === PersonType.NATURAL_PERSON;

    if (personType == PersonType.LEGAL_PERSON) {
      // Remover validadores de persona física
      this.formCustomer.controls['lastName'].clearValidators();
      this.formCustomer.controls['mothersLastName'].clearValidators();
      this.formCustomer.controls['secondName'].clearValidators();

      // Para persona moral, el campo name solo debe tener required, sin restricciones
      this.formCustomer.controls['name'].setValidators([Validators.required]);

      this.formCustomer.controls['lastName'].setValue(null, { emitEvent: false });
      this.formCustomer.controls['mothersLastName'].setValue(null, { emitEvent: false });

      this.formCustomer.controls['lastName'].setErrors(null);
      this.formCustomer.controls['mothersLastName'].setErrors(null);
      this.formCustomer.controls['secondName'].setErrors(null);
    } else if (isNaturalPerson) {
      // Aplicar validadores para persona física
      // Validar que no tenga espacios ni caracteres especiales
      const nameValidators = [Validators.required, naturalPersonNameValidator];
      const secondNameValidators = [naturalPersonNameValidator];
      const lastNameValidators = [Validators.required, naturalPersonNameValidator];
      const mothersLastNameValidators = [naturalPersonNameValidator];

      this.formCustomer.controls['name'].setValidators(nameValidators);
      this.formCustomer.controls['secondName'].setValidators(secondNameValidators);
      this.formCustomer.controls['lastName'].setValidators(lastNameValidators);
      this.formCustomer.controls['mothersLastName'].setValidators(mothersLastNameValidators);
    } else {
      // Si no hay tipo seleccionado o es otro tipo, solo aplicar required
      this.formCustomer.controls['name'].setValidators([Validators.required]);
      this.formCustomer.controls['lastName'].setValidators(Validators.required);
    }

    // Actualizar validación de todos los campos sin emitir eventos para evitar bucle infinito
    this.formCustomer.controls['name'].updateValueAndValidity({ emitEvent: false });
    this.formCustomer.controls['secondName'].updateValueAndValidity({ emitEvent: false });
    this.formCustomer.controls['lastName'].updateValueAndValidity({ emitEvent: false });
    this.formCustomer.controls['mothersLastName'].updateValueAndValidity({ emitEvent: false });
    this.formCustomer.updateValueAndValidity({ emitEvent: false });
  }

  allowAlphabeticCharacter(event: KeyboardEvent) {
    // Para persona moral, permitir todo sin restricciones
    // No hacer nada, permitir que el input acepte cualquier carácter
    return;
  }

  /**
   * Previene la escritura de espacios, números y caracteres especiales para persona física
   * Solo permite letras (incluyendo acentos y ñ)
   * Bloquea específicamente: espacios, números, #$%&( y otros caracteres especiales
   */
  allowOnlyLettersAndNumbers(event: KeyboardEvent) {
    // Usar event.key que es más confiable que event.which
    const key = event.key;

    // Permitir teclas de control (Backspace, Delete, Tab, Arrow keys, etc.)
    const controlKeys = [
      'Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End', 'Enter', 'Escape'
    ];

    if (controlKeys.includes(key)) {
      return; // Permitir teclas de control
    }

    // Permitir Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
    if (event.ctrlKey || event.metaKey) {
      return; // Permitir atajos de teclado
    }

    // Bloquear específicamente los caracteres prohibidos: #$%&(
    const forbiddenChars = /[#$%&(]/;
    if (forbiddenChars.test(key)) {
      event.preventDefault();
      return;
    }

    // Bloquear espacios
    if (key === ' ' || key === 'Space') {
      event.preventDefault();
      return;
    }

    // Bloquear números (0-9)
    if (/[0-9]/.test(key)) {
      event.preventDefault();
      return;
    }

    // Solo permitir letras (a-z, A-Z, acentos, ñ)
    const allowedPattern = /^[A-Za-zÀ-ÿ\u00f1\u00d1]$/;
    if (!allowedPattern.test(key)) {
      event.preventDefault();
    }
  }

  /**
   * Limpia el valor del input removiendo caracteres no permitidos
   * Se ejecuta en el evento input para limpiar cualquier carácter que haya pasado
   * Solo mantiene letras (incluyendo acentos y ñ), sin números ni espacios
   */
  cleanNaturalPersonInput(event: Event) {
    const target = event.target as HTMLInputElement;
    const currentValue = target.value || '';

    // Remover espacios, números, caracteres prohibidos (#$%&() y cualquier otro carácter especial
    // Solo mantener letras (incluyendo acentos y ñ)
    const cleanedValue = currentValue.replace(/[^A-Za-zÀ-ÿ\u00f1\u00d1]/g, '');

    // Si el valor cambió, actualizarlo
    if (cleanedValue !== currentValue) {
      target.value = cleanedValue;

      // Actualizar el valor del formulario
      const controlName = target.getAttribute('formControlName');
      if (controlName) {
        this.formCustomer.get(controlName)?.setValue(cleanedValue, { emitEvent: false });
        this.formCustomer.get(controlName)?.updateValueAndValidity();
      }
    }
  }

  /**
   * Limpia el contenido pegado, removiendo espacios, números y caracteres especiales
   * Solo mantiene letras (incluyendo acentos y ñ)
   * Remueve específicamente: espacios, números, #$%&( y otros caracteres especiales
   */
  onPasteNaturalPerson(event: ClipboardEvent) {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    // Remover espacios, números, caracteres prohibidos (#$%&() y otros caracteres especiales, mantener solo letras
    const cleanedText = pastedText.replace(/[^A-Za-zÀ-ÿ\u00f1\u00d1]/g, '');

    const target = event.target as HTMLInputElement;
    const start = target.selectionStart || 0;
    const end = target.selectionEnd || 0;
    const currentValue = target.value || '';

    // Insertar el texto limpio en la posición del cursor
    const newValue = currentValue.substring(0, start) + cleanedText + currentValue.substring(end);
    target.value = newValue;

    // Actualizar el valor del formulario
    const controlName = target.getAttribute('formControlName');
    if (controlName) {
      this.formCustomer.get(controlName)?.setValue(newValue);
      this.formCustomer.get(controlName)?.updateValueAndValidity();
    }

    // Restaurar la posición del cursor
    setTimeout(() => {
      target.setSelectionRange(start + cleanedText.length, start + cleanedText.length);
    }, 0);
  }

  validateDfsRating() {
    const control = this.formCustomer.get('dfsRating');
    if (control) {
      if (control.hasError('pattern')) {
        control.setValue(null);
      }
    }
  }

  private normalizeCustomerPatch(value: Partial<ClientApi> | null): any {
    if (!value) {
      return value;
    }
    const val = value as any;
    if (value.personType === PersonType.LEGAL_PERSON) {
      const customerName = val.customerName || val.firstName || val.name || '';
      return {
        ...value,
        name: customerName,
        customerName: customerName,
      };
    }
    if (value.personType === PersonType.NATURAL_PERSON) {
      const resolved = this.clientsService.resolveNaturalPersonNameFields(value);
      return {
        ...value,
        ...resolved,
        name: resolved.firstName || val.name || '',
        customerName: resolved.customerName ?? undefined,
      };
    }
    return value;
  }

  //#region CVA
  writeValue(value: any): void {
    if (value && this.formCustomer) {
      // Ensure feriaCustomer is converted to boolean when loading data
      const patchValue = { ...this.normalizeCustomerPatch(value) };
      if (this.featureLoyaltyFeriaQuotation && 'feriaCustomer' in patchValue) {
        patchValue.feriaCustomer = coerceOpenQuoteBoolean(patchValue.feriaCustomer);
      }
      this.formCustomer.patchValue(patchValue, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  private onChange = (_: any) => {};
  private onTouched = () => {};

  //#endregion

  //#region Validator
  validate(_control: AbstractControl<any, any>): ValidationErrors | null {
    return this.formCustomer.valid ? null : { invalidFormCustomer: true };
  }

  onEdit() {
    this.editingStarted.emit(this.customer);
  }
  //#endregion

  openEditClient() {
    this.tempSelectedClient.set(this.selectedClient());
    this.modalService.component = this.modalNewClient;
    this.modalService.openModal();
  }

  openModalNewClient() {
    this.tempSelectedClient.set(this.selectedClient());
    this.selectedClient.set(null);
    this.modalService.component = this.modalNewClient;
    this.modalService.openModal();
    if (this.document) {
      this.document.body.style.overflow = 'hidden';
    }
  }

  openModalSelectClient() {
    this.tempSelectedClient.set(this.selectedClient());
    this.modalService.component = this.modalSelectClient;
    this.modalService.openModal();
    if (this.document) {
      this.document.body.style.overflow = 'hidden';
    }
  }

  isLegalPerson() {
    return this.formCustomer.controls['personType']?.value == 'LEGAL_PERSON';
  }

  get isEditable() {
    return this.formCustomer.controls['isFromProLeaseNet']?.value == false && this.formCustomer.controls['rfc']?.value != null;
  }

  get personType() {
    return this.formCustomer.controls['personType']?.value || '';
  }

  get name() {
    return this.formCustomer.controls['name']?.value || '';
  }

  get lastName() {
    return this.formCustomer.controls['lastName']?.value || '';
  }

  get mothersLastName() {
    return this.formCustomer.controls['mothersLastName']?.value || '';
  }

  get rfc() {
    return this.formCustomer?.controls?.['rfc']?.value || '';
  }

  get formHasRFC() {
    return this.rfc !== null && this.rfc !== undefined && this.rfc.trim() !== '';
  }

  get secondName() {
    return this.formCustomer.controls['secondName']?.value || '';
  }

  /** Solo lectura: valor del check «Cliente de feria» en el formulario. */
  customerFeriaReadonlyYesNo(): string {
    if (!this.featureLoyaltyFeriaQuotation) {
      return '';
    }
    const raw = this.formCustomer?.getRawValue?.() as { feriaCustomer?: unknown } | undefined;
    return coerceOpenQuoteBoolean(raw?.feriaCustomer) ? 'Sí' : 'No';
  }

  onCheckboxChange(event: Event) {
    const target = event.target as HTMLInputElement;
    if(target.checked === this.isClientWithRFCCopy) {
      this.formCustomer.patchValue(this.customerCopy || {});
      if(this.customerIdCopy && this.customerIdCopy !== null) {
        this.selectedCustomerId.emit(this.customerIdCopy);
      }
      this.customerCopy = null;
      this.customerIdCopy = null;
    } else {
      this.isClientWithRFCCopy = this.isClientWithRFC();
      this.customerCopy = this.formCustomer.getRawValue();
      this.customerIdCopy = this.selectedClient()?.id;
      if(this.customerIdCopy && this.customerIdCopy !== null) {
        this.selectedCustomerId.emit(null as any);
      }
      this.formCustomer.patchValue({
        id: null,
        personType: null,
        name: null,
        secondName: null,
        mothersLastName: null,
        lastName: null,
        rfc: null,
        feriaCustomer: false,
      });
    }
    if(target.checked){
      this.formCustomer.controls['lastName'].setValidators(null);
      this.formCustomer.controls['lastName'].setErrors(null);
      this.formCustomer.controls['rfc'].setValidators(Validators.required);
      this.formCustomer.get('rfc')?.updateValueAndValidity();
    } else {
      this.formCustomer.controls['rfc'].clearValidators();
      this.formCustomer.get('rfc')?.updateValueAndValidity();
    }
    this.isClientWithRFC.set(target.checked);
    this.formCustomer.updateValueAndValidity();
  }
}
